/**
 * Interface translation.
 *
 * Hand-rolled rather than i18next: the dictionary is a plain typed object, so
 * `en` is declared as `Dictionary` and a forgotten key is a compile error
 * rather than a string that silently falls back to its own name. Interpolation
 * is done with functions for the same reason — the argument list is checked.
 *
 * Scope is the screen only. Invoices, the GoBD archive and the
 * Verfahrensdokumentation are addressed to German customers and to the
 * Finanzamt, so `src/export/` stays German whatever the interface says. The
 * same goes for number and date formatting: `12.345,67 €` on screen has to
 * match `12.345,67 €` in the PDF, and `14.07.2026` cannot become `07/14/2026`
 * in a tax record without inviting a misreading.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UiLanguage } from '@/domain/types';
import { de } from './de';
import { en } from './en';
import type { Dictionary } from './de';

export type { Dictionary };

const STORAGE_KEY = 'fahrtenbuch.language';

const DICTIONARIES: Record<UiLanguage, Dictionary> = { de, en };

export const LANGUAGE_NAMES: Record<UiLanguage, string> = {
  de: 'Deutsch',
  en: 'English',
};

/**
 * The language to start in, before any account is unlocked.
 *
 * Read from `localStorage` because the setup and login screens run outside the
 * session and have no key to decrypt settings with. German is the fallback
 * rather than the browser preference: the app is built around German tax law,
 * and the very first setup step asks for the language anyway.
 */
export function storedLanguage(): UiLanguage {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'de' || saved === 'en' ? saved : 'de';
}

interface LocaleValue {
  language: UiLanguage;
  t: Dictionary;
  setLanguage: (next: UiLanguage) => void;
}

const LocaleContext = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<UiLanguage>(storedLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo<LocaleValue>(
    () => ({
      language,
      t: DICTIONARIES[language],
      setLanguage: (next) => {
        localStorage.setItem(STORAGE_KEY, next);
        setLanguageState(next);
      },
    }),
    [language],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocaleContext(): LocaleValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('LocaleProvider fehlt.');
  return value;
}

/** The dictionary for the active language. */
export function useT(): Dictionary {
  return useLocaleContext().t;
}

export function useLanguage(): {
  language: UiLanguage;
  setLanguage: (next: UiLanguage) => void;
} {
  const { language, setLanguage } = useLocaleContext();
  return { language, setLanguage };
}
